/**
 * CRM › Service desk (v7 CORE-7a). The owner's Q4.1 types and Q4.2 SLA (Urgent 4 h · High 12 h · Normal 24 h · Low 72 h,
 * the clock paused while waiting for the customer).
 *   · queues: Open · Mine · Past SLA · Waiting · Resolved today, with the SLA timer on every row;
 *   · the detail pane: the conversation, the customer, the delivery it is about, same-lot tickets, a suggested resolution,
 *     and the actions — assign to me, reply, note, wait, resume, resolve (how), close, reopen within 7 days;
 *   · New ticket. A failed door opens its own Delivery ticket; complaint-type tickets keep writing the complaint log.
 * Also exports CustomerTickets for Customer 360 › Service.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import { rpc, supabase } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import { Page } from '../../shell/AppShell';
import { Button, ErrorState, LoadingRows, Panel, PanelHeader, cx, plainError } from '../../ui/primitives';
import { Field, Input, Select } from '../../ui/forms';
import { Drawer } from '../../ui/overlays';
import { toast } from '../../ui/toast';
import * as f from '../../ui/format';

interface Row {
  id: string; code: string; type: string; typeLabel: string | null; priority: string; status: string; subject: string; source: string; openedAt: string; slaDueAt: string;
  pastSla: boolean; minutesLeft: number | null; customer: { id: string; code: string; name: string; route: string | null } | null; owner: string | null; ownerId: string | null; resolution: string | null; lot: string | null;
}
interface Detail extends Row {
  events: { kind: string; body: string | null; from: string | null; to: string | null; at: string; by: string | null }[];
  delivery: { code: string; status: string; units: number; date: string; round: string } | null; sameLot: number | null; customerTickets90: number; suggestion: string | null;
}
interface Desk { counts: { open: number; mine: number; pastSla: number; waiting: number; resolvedToday: number }; rows: Row[] }
const QUEUES = [['OPEN', 'Open', 'open'], ['MINE', 'Mine', 'mine'], ['PAST_SLA', 'Past SLA', 'pastSla'], ['WAITING', 'Waiting', 'waiting'], ['RESOLVED_TODAY', 'Resolved today', 'resolvedToday'], ['ALL', 'All', null]] as const;
const PRIORITY_TONE: Record<string, string> = { URGENT: 'text-refused', HIGH: 'text-queued', NORMAL: 'text-ink', LOW: 'text-ink-muted' };

export function slaText(r: Pick<Row, 'status' | 'minutesLeft'>) {
  if (r.minutesLeft === null) return r.status === 'WAITING' ? 'paused' : '—';
  const m = Math.abs(r.minutesLeft); const t = m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
  return r.minutesLeft < 0 ? `${t} late` : `${t} left`;
}

function useVocab(domain: string) {
  return useQuery({ queryKey: ['reason-codes', domain], staleTime: 600_000, queryFn: async () => {
    const { data, error } = await supabase.from('reason_codes').select('code,label_en,status,sort_order').eq('domain', domain).eq('status', 'ACTIVE').order('sort_order');
    if (error) throw error;
    return (data ?? []) as { code: string; label_en: string }[];
  } });
}

export function ServiceDeskScreen() {
  const { can } = useSession();
  const [queue, setQueue] = useState<string>('OPEN');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const q = useQuery({ queryKey: ['service-desk', queue, search], refetchInterval: 60_000, queryFn: () => rpc<Desk>('service_desk', { p_queue: queue, p_search: search.trim() || null }) });
  return (
    <Page title="Service desk" subtitle="Complaints and requests as tickets, each with its time to resolve. A failed delivery opens its own."
      actions={can('service.case.manage') ? <Button variant="primary" onClick={() => setCreating(true)}><Plus size={14} aria-hidden /> New ticket</Button> : undefined}>
      <div className="mb-3 flex flex-wrap items-center gap-1">
        {QUEUES.map(([k, label, count]) => (
          <button key={k} type="button" aria-pressed={queue === k} onClick={() => setQueue(k)}
            className={cx('rounded-full border px-3 py-1 text-sm', queue === k ? 'border-brand bg-brand-soft text-brand' : 'border-line text-ink-muted hover:bg-surface-sunken',
              k === 'PAST_SLA' && (q.data?.counts.pastSla ?? 0) > 0 && 'text-refused')}>
            {label}{count && q.data ? ` · ${q.data.counts[count]}` : ''}</button>))}
        <label className="ml-auto flex items-center gap-1 rounded border border-line bg-surface px-2 py-1 text-sm"><Search size={14} className="text-ink-muted" aria-hidden />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ticket, customer or words" className="w-48 bg-transparent outline-none" aria-label="Search tickets" /></label>
      </div>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <Panel>
          {q.error ? <ErrorState error={q.error} retry={() => void q.refetch()} /> : !q.data ? <LoadingRows rows={6} columns={5} /> : (
            <div className="overflow-auto"><table className="w-full text-sm">
              <thead><tr className="text-left text-small text-ink-muted"><th className="px-4 py-1.5 font-normal">Ticket</th><th className="py-1.5 font-normal">About</th>
                <th className="py-1.5 font-normal">Customer</th><th className="py-1.5 font-normal">Status</th><th className="px-4 py-1.5 font-normal">SLA</th></tr></thead>
              <tbody>{q.data.rows.map((r) => (
                <tr key={r.id} onClick={() => setOpen(r.id)} className={cx('cursor-pointer border-t border-line hover:bg-surface-sunken', open === r.id && 'bg-brand-soft')}>
                  <td className="px-4 py-1.5"><div className="font-mono">{r.code}</div><div className={cx('text-small', PRIORITY_TONE[r.priority])}>{r.priority.toLowerCase()} · {r.typeLabel ?? r.type}</div></td>
                  <td className="py-1.5">{r.subject}{r.source.startsWith('AUTO') ? <span className="ml-1 text-small text-ink-muted">(by itself)</span> : null}</td>
                  <td className="py-1.5">{r.customer ? <><span className="font-mono">{r.customer.code}</span> {r.customer.name}</> : '—'}</td>
                  <td className="py-1.5">{r.status.toLowerCase().replace('_', ' ')}{r.owner ? <div className="text-small text-ink-muted">{r.owner}</div> : null}</td>
                  <td className={cx('px-4 py-1.5 whitespace-nowrap', r.pastSla ? 'font-medium text-refused' : 'text-ink-muted')}>{slaText(r)}</td>
                </tr>))}
                {q.data.rows.length === 0 ? <tr><td colSpan={5} className="px-4 py-4 text-ink-muted">Nothing in this queue.</td></tr> : null}
              </tbody>
            </table></div>)}
        </Panel>
        {open ? <TicketPane id={open} /> : <Panel className="p-6 text-sm text-ink-muted">Choose a ticket to see its conversation and act on it.</Panel>}
      </div>
      {creating ? <NewTicket onClose={() => setCreating(false)} onMade={(id) => { setCreating(false); setOpen(id); }} /> : null}
    </Page>
  );
}

function TicketPane({ id }: { id: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const [body, setBody] = useState(''); const [resolution, setResolution] = useState('');
  const q = useQuery({ queryKey: ['ticket', id], queryFn: () => rpc<Detail>('ticket', { p_ticket_id: id }) });
  const resolutions = useVocab('TICKET_RESOLUTION');
  const act = useMutation({
    mutationFn: (a: string) => rpc<{ detail: string }>('ticket_action', { p_ticket_id: id, p_action: a, p_body: body.trim() || null, p_resolution: a === 'RESOLVE' ? resolution : null, p_owner_id: null }),
    onSuccess: (r) => { toast(r.detail); setBody(''); void qc.invalidateQueries({ queryKey: ['ticket', id] }); void qc.invalidateQueries({ queryKey: ['service-desk'] }); },
    onError: (e) => toast(plainError((e as Error).message), { tone: 'bad' }),
  });
  const t = q.data;
  if (q.error) return <ErrorState error={q.error} />;
  if (!t) return <LoadingRows rows={5} columns={2} />;
  const may = can('service.case.manage');
  const live = !['RESOLVED', 'CLOSED'].includes(t.status);
  return (
    <Panel>
      <PanelHeader title={`${t.code} · ${t.subject}`} note={`${t.typeLabel ?? t.type} · ${t.priority.toLowerCase()} · ${t.status.toLowerCase().replace('_', ' ')} · ${slaText(t)}`} />
      <div className="grid gap-3 px-4 pb-4 text-sm">
        {t.customer ? <div><Link className="font-mono text-info underline" to={`/customers/${t.customer.id}`}>{t.customer.code}</Link> {t.customer.name}{t.customer.route ? ` · ${t.customer.route}` : ''}
          {t.customerTickets90 ? <span className="text-ink-muted"> · {t.customerTickets90} other ticket(s) in 90 days</span> : null}</div> : null}
        {t.delivery ? <div className="text-small text-ink-muted">Delivery {t.delivery.code} · {f.date(t.delivery.date)} · round {t.delivery.round} · {t.delivery.units} unit(s) · {t.delivery.status.toLowerCase()}</div> : null}
        {t.lot ? <div className="text-small">Lot <span className="font-mono">{t.lot}</span>{t.sameLot ? <span className="text-queued"> · same lot: {t.sameLot} other ticket(s)</span> : ''}</div> : null}
        {t.suggestion ? <div className="rounded border border-info px-2 py-1 text-small text-info">Suggested: {t.suggestion}</div> : null}
        <ol className="grid gap-2 border-l border-line pl-3">
          {t.events.map((e, i) => (
            <li key={i}><div className="text-small text-ink-muted">{f.dateTime(e.at)} · {e.by ?? 'system'} · {e.kind.toLowerCase()}{e.to && e.kind !== 'OPENED' ? ` → ${e.to.toLowerCase().replace('_', ' ')}` : ''}</div>
              {e.body ? <div>{e.body}</div> : null}</li>))}
        </ol>
        {may ? (
          <div className="grid gap-2 border-t border-line pt-3">
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} aria-label="Message"
              placeholder="What was said to the customer, or a note for the team" className="rounded-[var(--radius-control)] border border-line-strong bg-surface px-2 py-1" />
            <div className="flex flex-wrap gap-2">
              {live ? <>
                {!t.ownerId ? <Button size="dense" onClick={() => act.mutate('ASSIGN')}>Take it</Button> : null}
                <Button size="dense" disabled={body.trim().length < 2} onClick={() => act.mutate('REPLY')}>Reply</Button>
                <Button size="dense" disabled={body.trim().length < 2} onClick={() => act.mutate('NOTE')}>Note</Button>
                {t.status === 'WAITING' ? <Button size="dense" onClick={() => act.mutate('RESUME')}>Customer answered</Button>
                  : <Button size="dense" disabled={body.trim().length < 2} onClick={() => act.mutate('WAIT')}>Wait for customer</Button>}
              </> : null}
              {t.status === 'RESOLVED' ? <Button size="dense" onClick={() => act.mutate('CLOSE')}>Close</Button> : null}
              {!live ? <Button size="dense" onClick={() => act.mutate('REOPEN')}>Reopen</Button> : null}
            </div>
            {live ? (
              <div className="flex flex-wrap items-end gap-2">
                <Field label="Resolved by"><Select value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="Choose…"
                  options={(resolutions.data ?? []).map((r) => ({ value: r.code, label: r.label_en }))} /></Field>
                <Button size="dense" variant="primary" disabled={!resolution || act.isPending} onClick={() => act.mutate('RESOLVE')}>Resolve</Button>
              </div>) : null}
          </div>) : null}
      </div>
    </Panel>
  );
}

function NewTicket({ onClose, onMade }: { onClose: () => void; onMade: (id: string) => void }) {
  const [type, setType] = useState('DELIVERY'); const [priority, setPriority] = useState('NORMAL'); const [subject, setSubject] = useState('');
  const [customerCode, setCustomerCode] = useState(''); const [description, setDescription] = useState(''); const [lot, setLot] = useState('');
  const types = useVocab('TICKET_TYPE');
  const [key] = useState(() => crypto.randomUUID());
  const make = useMutation({
    mutationFn: async () => {
      let customerId: string | null = null;
      if (customerCode.trim()) {
        const { data, error } = await supabase.from('customers').select('id').eq('code', customerCode.trim().toUpperCase()).maybeSingle();
        if (error) throw error;
        if (!data) throw new Error(`No customer ${customerCode.trim().toUpperCase()}`);
        customerId = (data as { id: string }).id;
      }
      return rpc<{ id: string; code: string }>('open_ticket', { p_ticket: { type, priority, subject: subject.trim(), customerId, description: description.trim(), lot: lot.trim() || null, channel: 'PHONE' }, p_idempotency_key: key });
    },
    onSuccess: (r) => { toast(`Ticket ${r.code} opened.`, { tone: 'ok' }); onMade(r.id); },
  });
  return (
    <Drawer open onClose={onClose} title="New ticket" width="min(520px, 100vw)"
      footer={<div className="flex justify-end gap-2"><Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={subject.trim().length < 3 || make.isPending} onClick={() => make.mutate()}>Open ticket</Button></div>}>
      <div className="grid gap-3 p-4">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Type"><Select value={type} onChange={(e) => setType(e.target.value)} options={(types.data ?? []).map((t) => ({ value: t.code, label: t.label_en }))} /></Field>
          <Field label="Priority" hint="Urgent 4 h · High 12 h · Normal 24 h · Low 72 h"><Select value={priority} onChange={(e) => setPriority(e.target.value)}
            options={[{ value: 'URGENT', label: 'Urgent' }, { value: 'HIGH', label: 'High' }, { value: 'NORMAL', label: 'Normal' }, { value: 'LOW', label: 'Low' }]} /></Field>
        </div>
        <Field label="Customer code" hint="Optional"><Input value={customerCode} onChange={(e) => setCustomerCode(e.target.value)} placeholder="DSG-JDH-00001" className="font-mono" /></Field>
        <Field label="About" required><Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={140} placeholder="Milk tasted sour this morning" /></Field>
        <Field label="What the customer said"><textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-[var(--radius-control)] border border-line-strong bg-surface px-2 py-1 text-sm" /></Field>
        {type.endsWith('QUALITY') ? <Field label="Lot or bottle code" hint="From the cap or the bottle, if they have it"><Input value={lot} onChange={(e) => setLot(e.target.value)} className="font-mono" /></Field> : null}
        {make.error ? <p role="alert" className="text-small text-refused">{plainError((make.error as Error).message)}</p> : null}
      </div>
    </Drawer>
  );
}

/** Customer 360 › Service: this customer's tickets. */
export function CustomerTickets({ customerId }: { customerId: string }) {
  const q = useQuery({ queryKey: ['customer-tickets', customerId], queryFn: () => rpc<Row[]>('customer_tickets', { p_customer_id: customerId }) });
  if (!q.data) return null;
  return (
    <Panel>
      <PanelHeader title="Tickets" note={q.data.length ? `${q.data.filter((t) => !['RESOLVED', 'CLOSED'].includes(t.status)).length} open` : 'none'}
        actions={<Link className="text-small text-info underline" to="/customers/complaints">Service desk</Link>} />
      <ul className="grid gap-1.5 px-4 pb-4 text-sm">
        {q.data.slice(0, 10).map((t) => (
          <li key={t.id}><span className="font-mono">{t.code}</span> · {t.subject} · <span className={cx(t.pastSla && 'text-refused')}>{t.status.toLowerCase().replace('_', ' ')}{t.pastSla ? ' · past SLA' : ''}</span>
            <span className="text-small text-ink-muted"> · {f.date(t.openedAt)}</span></li>))}
        {q.data.length === 0 ? <li className="text-ink-muted">No ticket for this customer.</li> : null}
      </ul>
    </Panel>
  );
}
